/* eslint-disable functional/no-mixed-type */
import React, { ChangeEventHandler } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { styled, alpha } from '@mui/material/styles';
import { InputBase, TextField, Typography } from '@mui/material';
import { palette } from '../../theme/palette';

interface ISearchResultsAreaProps {
  show: boolean;
}

interface ISearchResultItem {
  id: string;
  name: string;
  source: string; // Assuming source is a string, adjust the type as necessary
}

interface ISearchComponentProps {
  handleInputChange: ChangeEventHandler<HTMLInputElement>;
  searchValue: string;
  showSearchResults?: boolean;
  searchResults?: ISearchResultItem[];
  onClick?: (item: ISearchResultItem) => void;
  isNavBarLayout?: boolean;
  disabled?: boolean;
}

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: '8px',
  backgroundColor: alpha(theme.palette.common.white, 0.1),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25)
  },
  marginLeft: 0,
  width: '100%',
  maxWidth: '420px',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1)
  }
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  left: 'calc(100% - 50px)',
  display: 'flex',
  alignItems: 'center',
  zIndex: 1000
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    fontSize: '18px',
    padding: '10px 12px 10px 16px',
    '&::placeholder': {
      color: 'white', // Change this to your desired placeholder color
      opacity: 1 // Optional: to make sure the placeholder color is fully opaque
    }
  }
}));

const StyledTextBase = styled(TextField)(({ theme }) => ({
  color: 'inherit',
  backgroundColor: '#ffff',
  borderRadius: theme.shape.borderRadius,
  width: '100%',
  '& .MuiInputBase-input': {
    fontSize: '18px',
    // vertical padding + font size from searchIcon
    paddingLeft: '16px',
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch'
      }
    }
  }
}));

const SearchResultsArea = styled('div')(({ show }: ISearchResultsAreaProps) => ({
  display: show ? 'flex' : 'none',
  flexDirection: 'column',
  position: 'absolute',
  top: '42px',
  backgroundColor: 'white',
  width: '100%',
  borderRadius: '0 0 12px 12px',
  boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
  maxHeight: '200px',
  overflowY: 'scroll',
  zIndex: '999'
}));

const SearchResultItem = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  height: '40px',
  padding: '8px 16px',
  paddingBlock: '0',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  minHeight: '40px',
  '&:hover': {
    backgroundColor: '#00000020'
  }
}));

const SearchResultItemTitle = styled(Typography)(() => ({
  fontSize: '18px',
  fontWeight: '600',
  color: palette.root.sub
}));

const SearchResultItemType = styled(Typography)(() => ({
  fontSize: '18px',
  fontWeight: 'normal',
  color: palette.root.link
}));

const SearchResultHeader = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  fontSize: '14px',
  color: palette.root.sub,
  padding: '8px 16px'
}));

const SearchResultFooterTitle = styled(Typography)(() => ({
  fontSize: '13px',
  fontWeight: 'normal',
  color: ' #666'
}));

const SearchComponent: React.FC<ISearchComponentProps> = ({
  handleInputChange,
  searchValue,
  showSearchResults = false,
  searchResults = [],
  onClick,
  isNavBarLayout = false,
  disabled = false
}) => {
  return (
    <Search sx={{ minWidth: 160, maxWidth: 420 }}>
      <SearchIconWrapper>
        <SearchIcon />
      </SearchIconWrapper>
      {isNavBarLayout ? (
        <StyledInputBase
          placeholder='Search…'
          inputProps={{ 'aria-label': 'search' }}
          onChange={handleInputChange}
          value={searchValue}
          disabled={disabled}
          data-testid={'search'}
        />
      ) : (
        <StyledTextBase
          placeholder='Search…'
          inputProps={{ 'aria-label': 'search' }}
          onChange={handleInputChange}
          value={searchValue}
          disabled={disabled}
        />
      )}
      <SearchResultsArea show={showSearchResults}>
        <SearchResultHeader>{searchResults?.length ?? 0} Results</SearchResultHeader>
        {searchResults?.map((item) => (
          <SearchResultItem key={item.id + item.source} onClick={() => onClick && onClick(item)}>
            <SearchResultItemTitle>{item.name}</SearchResultItemTitle>
            <SearchResultItemType>{item.source}</SearchResultItemType>
          </SearchResultItem>
        ))}
      </SearchResultsArea>
    </Search>
  );
};

export default SearchComponent;
